import { motion, useReducedMotion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

/**
 * Figma 59:8520 / 59:9907 / 59:8521 — headline, phone, strapline, each centred
 * on the 1400px canvas.
 *
 * The phone is footage with a real alpha channel, so the handset is already cut
 * out and sits straight on the page background. Nothing here clips it: the
 * rounded corners are part of the alpha, and rounding the box as well would
 * shave them a second time.
 *
 * The source is ProRes 4444 at 1080x1920 whose opaque area is a steady 759x1567
 * at (160,176); it ships cropped to that, one pixel larger on each axis so both
 * dimensions stay even for the encoders. Transparency needs two codecs — VP9 in
 * WebM everywhere, HEVC in MP4 for Safari — so the MP4 is listed first and
 * Safari takes it before reaching the WebM it cannot use.
 *
 * The numbers are written as utility classes rather than inline styles on
 * purpose: inline styles bypass the px-to-rem step, so the phone would have
 * stayed at its full size while the rest of the page scaled down around it.
 */
export function Hero() {
  const { t } = useTranslation();
  const still = useReducedMotion();

  return (
    <section className="flex flex-col items-center">
      <motion.h1
        className="w-full px-[16px] text-center font-display text-[40px] font-normal leading-[0.95] text-ink sm:text-[56px] lg:w-[817px] lg:px-0 lg:text-[96px] lg:leading-[0.9]"
        initial={still ? false : { opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      >
        {t('hero.titleLead')}
        <span className="font-semibold">{t('hero.titleAccent')}</span>
      </motion.h1>

      <motion.div
        className="relative mt-[32px] aspect-[760/1568] w-[min(78vw,394px)] lg:mt-[44px] lg:h-[813px] lg:w-[394px]"
        initial={still ? false : { opacity: 0, y: 40, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.9, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
      >
        {/* No poster on purpose: the clip opens from nothing, and any still we
            could show would be the static phone the design deliberately drops. */}
        <video
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          className="size-full"
        >
          <source src="/hero-alpha.mp4" type="video/mp4; codecs=hvc1" />
          <source src="/hero-alpha.webm" type="video/webm" />
        </video>
      </motion.div>

      <motion.p
        className="mt-[24px] w-full px-[20px] text-center text-[18px] font-medium leading-[1.2] text-ink sm:text-[24px] lg:mt-[44px] lg:w-[623px] lg:whitespace-nowrap lg:px-0 lg:text-[32px] lg:leading-[0.9]"
        initial={still ? false : { opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, delay: 0.35, ease: [0.22, 1, 0.36, 1] }}
      >
        {t('hero.subtitle')}
      </motion.p>
    </section>
  );
}
